/**
 * Rakuten Loyalty Exemption Signal — consent re-capture.
 *
 * A ranLoyalty=0 click is deliberately not stored on arrival: the affiliate cookie waits for
 * marketing consent. But that consent is given here in the browser, on the landing page, without a
 * further request to the server — and ranSiteID exists only in that page's query string. Without
 * this, a visitor who accepts a few seconds after landing would go unattributed for good, and their
 * publisher uncredited.
 *
 * A consent-blocked third-party tag gets this for free: the consent manager re-runs it on the same
 * page once consent arrives. This is the equivalent for a server-side integration.
 */

const TRACK_ENDPOINT = '/rakuten/track';
const TRACKING_ID_PARAM = 'ranSiteID';
const MID_PARAM = 'ranMID';
const LOYALTY_PARAM = 'ranLoyalty';
const LOYALTY_EXEMPT = '1';
const CONSENT_COOKIE = 'CookieConsent=';

let sent = false;

const recapture = async (params: URLSearchParams): Promise<void> => {
  if (sent) {
    return;
  }

  // The server decides from Cookiebot's own cookie, so a request that beat it there would store
  // nothing at all — and no later event would retry, because this one already ran. Waiting until
  // the cookie is visible here keeps the two in step.
  if (!window.Cookiebot?.consent?.marketing || !document.cookie.includes(CONSENT_COOKIE)) {
    return;
  }

  sent = true;

  const query = new URLSearchParams();
  query.set(TRACKING_ID_PARAM, params.get(TRACKING_ID_PARAM) ?? '');

  const mid = params.get(MID_PARAM);
  if (mid) {
    query.set(MID_PARAM, mid);
  }

  const loyalty = params.get(LOYALTY_PARAM);
  if (loyalty) {
    query.set(LOYALTY_PARAM, loyalty);
  }

  try {
    // keepalive: accepting the banner and clicking straight through is a normal thing to do.
    await fetch(`${TRACK_ENDPOINT}?${query.toString()}`, {
      credentials: 'same-origin',
      keepalive: true
    });
  } catch {
    /* attribution only — nothing here is worth surfacing to the visitor */
  }
};

/** Registers the listeners. Cheap enough to call on every page; it exits immediately off a click. */
export function prepareRakutenConsentRecapture(): void {
  const params = new URLSearchParams(window.location.search);

  // Nothing to re-capture: not an affiliate landing, or an exempt click the server already stored.
  if (!params.get(TRACKING_ID_PARAM) || params.get(LOYALTY_PARAM) === LOYALTY_EXEMPT) {
    return;
  }

  const onConsent = (): void => {
    void recapture(params);
  };

  window.addEventListener('CookiebotOnAccept', onConsent);
  window.addEventListener('CookiebotOnConsentReady', onConsent);
}
